/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useMemo } from "react";
import dayjs from "dayjs";
import {
  Maybe,
  useApplicationsPaginatedByRecordNidQuery,
} from "~/generated/graphql";
import { dateFormat } from "~/constants";
import { useCurrency } from "~/hooks";
import { mapApplicationWithEnvironment } from "~/tools/applicationMapper";

type Detail = {
  label: string;
  value: string;
  clipboard?: boolean;
  target?: string;
  url?: string | null;
  urlTooltip?: string | null;
};

type CostData = {
  id: string;
  providerType: string;
  provider: string;
  createdAt?: Maybe<string>;
  validThrough?: Maybe<string>;
};

export function mapCostDetails<T extends CostData>(data: T): Detail[] {
  return [
    {
      label: "MCA ID",
      value: data.id,
      url: null,
      clipboard: true,
    },
    {
      label: "Resource Name",
      value: data.providerType,
      url: null,
      clipboard: true,
    },
    {
      label: `${data.provider} ID`,
      value: "-",
      url: null,
      clipboard: true,
      urlTooltip: null,
    },
    {
      label: `${data.provider} type`,
      value: data.providerType || "-",
      url: null,
    },
    {
      label: "Account",
      value: "-",
      target: "",
    },
    {
      label: "Location",
      value: "global",
      url: null,
    },
  ];
}

type CloudData = CostData & {
  name: string;
  providerId: string;
  cloudAccountId: string;
  cloudAccountName?: string;
  cloudAccountUrl?: string;
  region: string;
  url?: string;
  urlTooltip?: string;
};

export function mapCloudDetails<T extends CloudData>(data: T): Detail[] {
  return [
    {
      label: "MCA ID",
      value: data.id,
      url: null,
      clipboard: true,
    },
    {
      label: "Resource Name",
      value: data.name,
      url: null,
      clipboard: true,
    },
    {
      label: `${data.provider} ID`,
      value: data.providerId,
      url: data.url,
      clipboard: true,
      urlTooltip: data.urlTooltip,
    },
    {
      label: `${data.provider} type`,
      value: data.providerType ?? "-",
      url: null,
    },
    {
      label: "Account",
      value: data.cloudAccountName ?? data.cloudAccountId,
      target: data.cloudAccountUrl,
    },
    {
      label: "Location",
      value: data.region,
      url: null,
    },
  ];
}

export function mapExternalIntegrationDetails<T extends CloudData>(
  data: T
): Detail[] {
  return [
    {
      label: "MCA ID",
      value: data.id,
      clipboard: true,
    },
    {
      label: "Record ID",
      value: data.providerId,
      clipboard: true,
    },
    {
      label: "Record Name",
      value: data.name,
      clipboard: true,
    },

    {
      label: "Record Type",
      value: data.providerType ?? "-",
    },
    {
      label: "Creation Date",
      value: dayjs(data.createdAt ?? "").format(dateFormat.dayMonthYear),
    },
    ...(data.validThrough
      ? [
          {
            label: "Closing Date",
            value: dayjs(data.validThrough).format(dateFormat.monthYear),
          },
        ]
      : []),
  ];
}

export function useMappedApplications(
  variables: Parameters<
    typeof useApplicationsPaginatedByRecordNidQuery
  >[0]["variables"]
) {
  const { data, ...result } = useApplicationsPaginatedByRecordNidQuery({
    variables,
    errorPolicy: "all",
  });

  const { currency } = useCurrency();

  const count = data?.applicationsPaginated?.count ?? 0;
  const applications = useMemo(
    () =>
      data?.applicationsPaginated?.results?.map((application) =>
        mapApplicationWithEnvironment(application, currency)
      ) ?? [],
    [currency, data?.applicationsPaginated?.results]
  );

  return {
    applications,
    count,
    ...result,
  };
}
